import { Dropdown, Menu, Popover } from 'antd';
import React, { Fragment, ReactNode, useState } from 'react';
import NewProfile from './NewProfile';
import {
    amIDoctor,
    getDoc,
    getDocAccounts,
    getMe,
    getSelectedClinicId,
    getSingleDoctor,
    isSingleDoctorAccess,
} from '../../store/idbHandler';
import { useHistory } from 'react-router-dom';
import {
    GO_TO_BOOK_TRAINING,
    GO_TO_LOGOUT,
    GO_TO_MY_CALENDAR,
    GO_TO_MY_GENIE,
    GO_TO_MY_KHATA,
    GO_TO_MY_PROFILE,
    GO_TO_MY_RX_TEMPLATE,
    GO_TO_PATIENT_REFERRAL,
    GO_TO_SETTINGS,
    GO_TO_SUBSCRIBE,
} from '../../routes/paths';
import {
    BookTrainingComponent,
    DoctorReferralComponent,
    DownloadPtDBComponent,
    KhataComponent,
    MyAvailabilityComponent,
    MyGenieComponent,
    MyProfileComponent,
    MyRXTemplateComponent,
    SettingsComponent,
    SuccessManagerComponent,
    SupportTicketsComponent,
    TroubleshootMySystemComponent,
} from './NewProfileComponents';
import { downloadPtDir, showDialog } from '../../store/actions/prescriptionPad';
import { useDispatch, useSelector } from 'react-redux';
import {
    toggleLogoutModal,
    togglePrivacyDialog,
    toggleStorageModal,
} from '../../store/actions/session';
import { getIsStorageInfo, getKhataCredits } from '../../store/selectors/session';
import StorageInfo from './StorageInfo';
import { StorageInfoResponse } from '../../api/fetchStorageInfo';
import { businessLogo } from './Icons';
import { AccountResponse } from '../../api/fetchDocAccounts';
import AccountSwitchComponent from './AccountSwitchComponent';
import { addAlpha, getColorForString } from '../../pages/utils';
import { NewSwitchProfileDropdown } from './NewSwitchProfileDropdown';
import CSCard from './CSCard';
import { TrialChip } from './NavBar';
import moment from 'moment';
import { track } from '../../tracking';
import { SREV2 } from '../../api/pushGenie';
import { getAclViewClassName } from '../../restrictions/utils';

interface Props {
    title: string;
    filterComponent?: ReactNode;
    hideProfileDropdown?: boolean;
    hideSwitchButton?: boolean;
}

interface NewProfileProps {
    dName: string;
    imgUrl: string;
    className?: string;
    inviteCode: string | undefined;
    message: string;
    showInviteCode: boolean;
    showInviteLink: boolean;
}

const gotoMyProfile = (history: any) => history.push(GO_TO_MY_PROFILE);
const gotoMyGenie = (history: any) => history.push(GO_TO_MY_GENIE('genie-configuration'));
const gotoMyPatientReferral = (history: any) => history.push(GO_TO_PATIENT_REFERRAL);

const gotoMyRXTemplate = (history: any) => history.push(GO_TO_MY_RX_TEMPLATE);
const gotoSubscribe = (history: any) => history.push(GO_TO_SUBSCRIBE);
const gotoMyAvailability = (history: any) => history.push(GO_TO_MY_CALENDAR);
const gotoSettings = (history: any) => history.push(GO_TO_SETTINGS('invite'));
export const gotoLogout = (history: any, clear: boolean) =>
    history.push({ pathname: GO_TO_LOGOUT, search: `?clear=${clear}` });
const gotoRequestTraining = (history: any) => history.push(GO_TO_BOOK_TRAINING);
const onPrivacy = (dispatch: any) => {
    dispatch(togglePrivacyDialog(true));
};
const gotoMyKhata = (history: any) => history.push(GO_TO_MY_KHATA('all-transactions'));

const MenuItem: React.FC<{ isNew?: boolean; handleClick: any; aclViewName?: string }> = (props) => {
    const { handleClick } = props;

    return (
        <Menu.Item
            className={`py-5 ${props.isNew ? 'px-16' : 'pl-16 pr-8'} ${
                props.aclViewName ? getAclViewClassName(props.aclViewName) : ''
            }`}
            onClick={handleClick}
        >
            {props.children}
        </Menu.Item>
    );
};

const menu = (
    payload: NewProfileProps,
    history: any,
    exportAsExcel: () => void,
    dispatch: any,
    accounts: AccountResponse['data'] | null | undefined,
    toggleShowAccount: (val: boolean) => void,
    showSupportTicketsHandler: () => void,
    showTroubleshootMySystemHandler: () => void,
    onLogOut: () => void,
    storageInfo?: StorageInfoResponse,
) => {
    const doc = getSingleDoctor();
    const sass = doc?.profile.professional.saas;
    const plan = sass ? (sass === 'APT' ? 'PLUS' : 'PRO') : 'TRAIL';
    const hasSM = !!(
        doc?.profile.professional.cs &&
        doc?.profile.professional.cs?.success_manager &&
        doc?.profile.professional.cs?.oid
    );
    const LS_OPEN_SM_DIALOG_ON_MOUNT = 'showSmDialogOnMount';
    const smDialogDateString = localStorage.getItem(LS_OPEN_SM_DIALOG_ON_MOUNT);

    const isWithin15DaysFromPayment =
        moment().diff(moment(doc?.profile.professional.saas_detail?.start_date), 'days') <= 15;

    const closeDate = smDialogDateString ? moment(smDialogDateString) : null;

    const hasHfr = doc?.profile.professional.clinics?.find((c) => c._id === getSelectedClinicId())
        ?.abdm?.hfr_id;
    const openSmPopoverOnFirstMount =
        !hasHfr &&
        isWithin15DaysFromPayment &&
        (smDialogDateString === null ||
            (closeDate ? !moment().startOf('day').isSame(closeDate.startOf('day')) : true));

    return (
        <Menu className="flex flex-col rounded-8 p-0 profile-dropdown-overlay">
            {payload.dName && (
                <div
                    key="profile_item"
                    className="cursor-pointer px-12 py-6"
                    onClick={() => toggleShowAccount(true)}
                >
                    <NewProfile
                        dName={payload.dName}
                        imgUrl={payload.imgUrl}
                        accounts={accounts}
                        inviteCode={payload.inviteCode}
                        message={payload.message}
                        showInviteCode={payload.showInviteCode}
                        showInviteLink={payload.showInviteLink}
                    />
                </div>
            )}
            <div className="overflow-scroll hide-scrollbar profile-dropdown-menu">
                <div className="border-t-1 border-neutral-7">
                    <MenuItem
                        key="go_to_profile_item"
                        handleClick={() => gotoMyProfile(history)}
                        aclViewName={SREV2.PROFILE_MANAGEMENT}
                    >
                        <MyProfileComponent />
                    </MenuItem>
                    {(isSingleDoctorAccess() || amIDoctor()) && (
                        <MenuItem
                            key="go_to_genie_item"
                            handleClick={() => gotoMyGenie(history)}
                            aclViewName={SREV2.GENIE_DASHBOARD}
                        >
                            <MyGenieComponent />
                        </MenuItem>
                    )}
                    {amIDoctor() && (
                        <MenuItem
                            key="go_to_doctor_referral_dashboard"
                            handleClick={() => gotoMyPatientReferral(history)}
                            aclViewName={SREV2.PATIENT_REFERRALS_DASHBOARD}
                        >
                            <DoctorReferralComponent />
                        </MenuItem>
                    )}
                    <MenuItem
                        key="go_to_rx_template_item"
                        handleClick={() => gotoMyRXTemplate(history)}
                        aclViewName={SREV2.CUSTOMIZE_RX_TEMPLATE}
                    >
                        <MyRXTemplateComponent />
                    </MenuItem>

                    <MenuItem
                        key="go_to_my_availability_item"
                        handleClick={() => gotoMyAvailability(history)}
                        aclViewName={SREV2.AVAILABILITY}
                    >
                        <MyAvailabilityComponent />
                    </MenuItem>
                    <MenuItem
                        key="go_to_my_khata"
                        handleClick={() => gotoMyKhata(history)}
                        aclViewName={SREV2.EKA_CREDITS}
                    >
                        <KhataComponent />
                    </MenuItem>
                </div>

                <div key="go_to_book_training_item" className="border-t-1 border-neutral-7">
                    <div>
                        <Popover
                            content={
                                <CSCard
                                    doc={doc}
                                    plan={plan}
                                    planIcon={
                                        <TrialChip
                                            className={{
                                                plus: 'w-46 h-28',
                                                pro: 'w-41 h-28',
                                                trail: 'w-46 h-28',
                                            }}
                                        />
                                    }
                                    hasSm={hasSM}
                                />
                            }
                            title={null}
                            trigger="click"
                            placement="left"
                            overlayClassName="csm-card z-50"
                            defaultVisible={openSmPopoverOnFirstMount}
                            onVisibleChange={(val) => {
                                if (!val) {
                                    const value = moment().toISOString();
                                    localStorage.setItem(LS_OPEN_SM_DIALOG_ON_MOUNT, value);
                                    track('CSM_POPOVER_CLOSED', { dateString: value });
                                }
                            }}
                        >
                            <button
                                key="success_manager"
                                className="py-5 pl-16 pr-8"
                                onClick={() => {
                                    track('CUSTOMER_SUCCESS_CARD_BTN', {
                                        on: 'Navbar',
                                        plan: plan,
                                        saas: sass,
                                    });
                                }}
                            >
                                <SuccessManagerComponent hasSM={hasSM} />
                            </button>
                        </Popover>
                        <style>
                            {`
                                .csm-card .ant-popover-inner {
                                    padding: 16px;
                                    border-radius: 0.5rem;
                                    bottom: 8px;
                                    position: relative;
                                    background: ${
                                        plan === 'TRAIL'
                                            ? '#1A1A1A'
                                            : plan === 'PLUS'
                                            ? 'linear-gradient(92deg, #D3DFFF 4.12%, #E9EFFF 51.37%, #D3DFFF 98.62%);'
                                            : 'linear-gradient(92deg, #373737 4.12%, #1A1A1A 51.37%, #373737 98.62%);'
                                    }
                                }
                            `}
                        </style>
                    </div>
                    <MenuItem
                        handleClick={() => gotoRequestTraining(history)}
                        aclViewName={SREV2.REQUEST_TRAINING}
                    >
                        <BookTrainingComponent />
                    </MenuItem>
                    <MenuItem handleClick={showSupportTicketsHandler}>
                        <SupportTicketsComponent />
                    </MenuItem>
                </div>
                <div
                    key="go_to_troubleshoot_my_system_item"
                    className="border-t-1 border-neutral-7"
                >
                    <MenuItem handleClick={showTroubleshootMySystemHandler}>
                        <TroubleshootMySystemComponent />
                    </MenuItem>
                </div>
                <div key="go_to_settings_item">
                    <MenuItem
                        handleClick={() => gotoSettings(history)}
                        aclViewName={SREV2.SETTINGS_AND_PREFERENCES}
                    >
                        <SettingsComponent />
                    </MenuItem>
                </div>
                {storageInfo && (
                    <div key="go_to_storage_item" className="border-t-1 border-neutral-7">
                        <Menu.Item
                            className="p-8 m-0"
                            onClick={() => dispatch(toggleStorageModal(true))}
                        >
                            <StorageInfo
                                className="flex flex-col gap-4"
                                storage={storageInfo.storage}
                                outerClassName="flex gap-8 items-start px-6 bg-white"
                                statusClassName="text-darwin-neutral_new-1000 font-400"
                                iconCssClassName="mt-4"
                            />
                        </Menu.Item>
                    </div>
                )}

                <div
                    key="go_to_download_pt_item"
                    className={`border-t-1 border-neutral-7 ${getAclViewClassName(
                        SREV2.DOWNLOAD_PATIENT_EXCEL,
                    )}`}
                >
                    <MenuItem handleClick={exportAsExcel}>
                        <DownloadPtDBComponent />
                    </MenuItem>
                </div>
            </div>

            <Menu.Item
                key="logout_item"
                className="border-t-1 border-neutral-7 py-5 px-16"
                onClick={onLogOut}
            >
                <div className="flex items-center gap-8 py-4">
                    <svg
                        width="17"
                        height="14"
                        viewBox="0 0 17 14"
                        className="fill-red-primary"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <path
                            d="M6 13.25C6 13.6875 5.65625 14 5.25 14H3C1.3125 14 0 12.6875 0 11V3C0 1.34375 1.3125 0 3 0H5.25C5.65625 0 6 0.34375 6 0.75C6 1.1875 5.65625 1.5 5.25 1.5H3C2.15625 1.5 1.5 2.1875 1.5 3V11C1.5 11.8438 2.15625 12.5 3 12.5H5.25C5.65625 12.5 6 12.8438 6 13.25ZM15.7812 6.5L11.8125 2.25C11.5312 1.9375 11.0625 1.9375 10.75 2.21875C10.4375 2.5 10.4375 2.96875 10.7188 3.28125L13.5 6.25H5.71875C5.3125 6.25 5 6.59375 5 7C5 7.4375 5.3125 7.75 5.71875 7.75H13.4688L10.6562 10.75C10.375 11.0625 10.375 11.5312 10.6875 11.8125C10.875 11.9375 11.0625 12 11.2188 12C11.4062 12 11.5938 11.9375 11.75 11.7812L15.7188 7.53125C16.0625 7.25 16.0625 6.78125 15.7812 6.5Z"
                            className="fill-red-primary"
                        />
                    </svg>
                    <p className="text-13 text-red-primary">Log Out</p>
                    <style>
                        {`
                        .ant-dropdown-menu-item.ant-dropdown-menu-item-danger:hover {
                            color: #ff4d4f;
                            background-color: white;
                        }
                    `}
                    </style>
                </div>
            </Menu.Item>
            <Menu.Item
                className="border-t-1 rounded-b-8 bg-neutral-9 border-neutral-7 py-5 px-16"
                onClick={() => onPrivacy(dispatch)}
                key="go_to_privacy_item"
            >
                <div className="flex gap-16 items-center justify-center">
                    <p className="text-11">Privacy Policy</p>
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="4"
                        height="4"
                        viewBox="0 0 4 4"
                        fill="none"
                    >
                        <path
                            d="M2 4C0.890625 4 0 3.10938 0 2C0 0.898438 0.890625 0 2 0C3.10156 0 4 0.898438 4 2C4 3.10938 3.10156 4 2 4Z"
                            fill="#767676"
                        />
                    </svg>
                    <p className="text-11">Terms & Conditions</p>
                </div>
            </Menu.Item>
        </Menu>
    );
};

const ProfileDropdownComponent: React.FC<{
    props: NewProfileProps;
    accounts: AccountResponse['data'];
}> = ({ props, accounts }) => {
    const me = getMe();
    const doc = me?.doctor;

    const storageInfo = useSelector(getIsStorageInfo);
    const khataCredits = useSelector(getKhataCredits);

    const history = useHistory();
    const dispatch = useDispatch();

    const exportAsExcel = (): void => {
        dispatch(downloadPtDir());
    };

    const [showAccounts, setShowAccounts] = useState(false);

    const toggleShowAccount = (val: boolean) => setShowAccounts(val);

    const [visible, setVisible] = useState(true);

    const closeDropdown = () => setVisible(false);

    const currAccountBusiness =
        accounts && accounts.length > 0
            ? accounts.filter((d) => d.business).find((d: any) => d['_id'] === getDoc()?._id)
                  ?.business?.id
            : undefined;

    const showSupportTicketsHandler = () => {
        dispatch(showDialog('support-tickets'));
    };

    const showTroubleshootMySystemHandler = () => {
        dispatch(showDialog('troubleshoot-my-system'));
    };

    const isKhataCreditVisible = khataCredits !== undefined && !isNaN(khataCredits);

    const onLogOut = () => {
        dispatch(toggleLogoutModal(true));
    };

    return visible ? (
        <Dropdown
            placement="bottomRight"
            overlayClassName="z-50"
            overlay={() =>
                showAccounts ? (
                    <AccountSwitchComponent
                        accounts={accounts}
                        dName={props.dName}
                        toggleShowAccount={toggleShowAccount}
                        closeDropDown={closeDropdown}
                    />
                ) : (
                    menu(
                        props,
                        history,
                        exportAsExcel,
                        dispatch,
                        accounts,
                        toggleShowAccount,
                        showSupportTicketsHandler,
                        showTroubleshootMySystemHandler,
                        onLogOut,
                        storageInfo,
                    )
                )
            }
            trigger={['click']}
            destroyPopupOnHide
        >
            <div
                className={`bg-darwin-neutral_new-50 h-36 cursor-pointer flex items-center gap-8 text-neutral-black font-400 text-13 justify-between px-8 rounded-8 ${
                    props.className ? props.className : ''
                }`}
                style={{ width: isKhataCreditVisible ? 280 : 220 }}
            >
                {currAccountBusiness && <span className="mr-2">{businessLogo()}</span>}
                {isSingleDoctorAccess() &&
                (doc?.profile.personal.pic || getMe()?.docs?.[0]?.personal.pic) ? (
                    <img
                        className="rounded-4 h-24 w-24 flex-shrink-0"
                        src={doc?.profile.personal.pic || getMe()?.docs?.[0]?.personal.pic}
                        alt="profile photo"
                    />
                ) : (
                    <div
                        className="flex items-center justify-center w-28 h-28 leading-6 text-20 font-700 rounded-64"
                        style={{
                            color: getColorForString(getMe()?.user.fn + 'random'),
                            backgroundColor: addAlpha(getColorForString(getMe()?.user.fn), 0.1),
                        }}
                    >
                        {getMe()?.user.fn[0]}
                    </div>
                )}
                {props.dName && (
                    <p className="overflow-ellipsis text-13 font-700 overflow-hidden whitespace-nowrap">
                        {props.dName}
                    </p>
                )}
                <p
                    className={`font-700 py-2 px-4 text-8 rounded-3 ${
                        me?.loginmeta?.role?.toLowerCase() === 'doctor' && getDoc()?._id
                            ? 'bg-darwin-accent-bright-blue-800 text-white'
                            : 'bg-darwin-accent-bright-yellow-800'
                    }`}
                    style={{ fontSize: 8 }}
                >
                    {me?.loginmeta?.role?.toLowerCase() === 'doctor' && getDoc()?._id
                        ? me?.loginmeta?.role
                        : 'STAFF'}
                </p>
                {isKhataCreditVisible && (
                    <div
                        className={`flex items-center space-x-4 py-4 px-6 text-white font-700 text-10 rounded-4  ${
                            Number(khataCredits) <= 20
                                ? 'bg-darwin-status-warning-500'
                                : 'bg-darwin-green-primary'
                        } ${getAclViewClassName(SREV2.EKA_CREDITS)}`}
                    >
                        <p>
                            <svg
                                className="w-12 h-12 fill-white"
                                viewBox="0 0 16 16"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    fill-rule="evenodd"
                                    clip-rule="evenodd"
                                    d="M8.03122 2.74858C5.1112 2.74858 2.74916 5.10254 2.74916 8C2.74916 10.8975 5.1112 13.2514 8.03122 13.2514C10.3332 13.2514 12.2904 11.7875 13.0142 9.74696C12.8241 9.88866 12.6182 10.0509 12.3975 10.2248C12.2727 10.3232 12.1432 10.4253 12.009 10.5295C11.5588 10.8794 11.0585 11.252 10.6022 11.4909C10.0494 11.7803 9.39978 11.9248 8.7666 11.753C8.2107 11.6022 7.74939 11.2301 7.41994 10.667C6.99823 10.8928 6.49597 10.9781 5.96176 10.7934C5.52268 10.6415 5.17287 10.2771 5.04724 9.82211C4.91364 9.3382 5.04805 8.81162 5.46191 8.39795C5.97042 7.88967 6.69391 7.90078 7.25326 8.21478C7.4031 8.2989 7.54413 8.40457 7.67395 8.52986C7.79368 8.28279 7.88849 8.02394 7.95232 7.78184C7.97162 7.70866 7.98727 7.64011 7.99979 7.57649H5.82665C5.4817 7.57649 5.20207 7.29698 5.20207 6.9522C5.20207 6.60741 5.4817 6.3279 5.82665 6.3279H7.88536C7.87167 6.26648 7.85718 6.20441 7.84182 6.14243C7.80879 6.00911 7.77509 5.89086 7.74184 5.79436H5.65441C5.30946 5.79436 5.02982 5.51486 5.02982 5.17007C5.02982 4.82528 5.30946 4.54578 5.65441 4.54578H10.542C10.887 4.54578 11.1666 4.82528 11.1666 5.17007C11.1666 5.51486 10.887 5.79436 10.542 5.79436H9.04232C9.04641 5.81042 9.05042 5.8264 9.05436 5.84229C9.09447 6.00419 9.13072 6.17348 9.16176 6.3279H10.5422C10.8871 6.3279 11.1667 6.60741 11.1667 6.9522C11.1667 7.29698 10.8871 7.57649 10.5422 7.57649H9.26531C9.24216 7.75209 9.20524 7.92937 9.16024 8.10003C9.04318 8.54398 8.84973 9.02461 8.59217 9.45734C8.52961 9.56245 8.4618 9.66717 8.38869 9.76946C8.4004 9.80993 8.41147 9.85087 8.42189 9.89239C8.62751 10.3231 8.87778 10.4894 9.09375 10.548C9.33785 10.6142 9.65929 10.5751 10.0226 10.3848C10.3679 10.2041 10.7879 9.89703 11.2423 9.54382C11.3571 9.45459 11.4747 9.36184 11.593 9.26867C11.9199 9.01106 12.2508 8.7503 12.5355 8.55238C12.7298 8.4173 12.9361 8.28723 13.1324 8.20179C13.231 8.15886 13.351 8.1165 13.4819 8.09813C13.6123 8.07984 13.7956 8.07971 13.9819 8.1703C14.4075 8.37726 14.4512 8.82062 14.4469 9.03833C14.442 9.2918 14.3737 9.59421 14.2692 9.93091L14.2688 9.93221C13.4417 12.5795 10.9614 14.5 8.03122 14.5C4.42695 14.5 1.5 11.5926 1.5 8C1.5 4.40734 4.42695 1.5 8.03122 1.5C11.1159 1.5 13.698 3.64987 14.3839 6.51088C14.4643 6.84635 14.223 7.16316 13.8813 7.2108C13.5397 7.25844 13.2278 7.01844 13.1397 6.68497C12.5436 4.4295 10.4807 2.74858 8.03122 2.74858ZM6.89636 9.52726C6.80915 9.41977 6.71965 9.34726 6.64155 9.30342C6.44962 9.19567 6.3678 9.25825 6.34521 9.28083C6.2801 9.34591 6.25923 9.39522 6.25201 9.42092C6.24475 9.44682 6.24556 9.46882 6.25139 9.48995C6.26556 9.54126 6.31251 9.59345 6.37022 9.61341C6.54192 9.67277 6.70307 9.651 6.89289 9.52949L6.89636 9.52726ZM13.1973 8.987C13.1973 8.987 13.1978 8.98953 13.198 8.99558C13.1973 8.99019 13.1973 8.987 13.1973 8.987Z"
                                />
                            </svg>
                        </p>
                        {typeof khataCredits === 'number' && <p>{khataCredits}</p>}
                    </div>
                )}
                <svg
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <path
                        d="M13.2812 6.45703L8.43164 11.1035C8.2793 11.2305 8.12695 11.2812 8 11.2812C7.84766 11.2812 7.69531 11.2305 7.56836 11.1289L2.69336 6.45703C2.43945 6.22852 2.43945 5.82227 2.66797 5.59375C2.89648 5.33984 3.30273 5.33984 3.53125 5.56836L8 9.83398L12.4434 5.56836C12.6719 5.33984 13.0781 5.33984 13.3066 5.59375C13.5352 5.82227 13.5352 6.22852 13.2812 6.45703Z"
                        fill="#1A1A1A"
                    />
                </svg>
            </div>
        </Dropdown>
    ) : null;
};

export const ProfileDropdown: React.FC<NewProfileProps> = (props) => {
    const accounts = getDocAccounts();

    return <ProfileDropdownComponent accounts={accounts} props={props} />;
};

export const SwitchProfileDropdown: React.FC = () => {
    return <NewSwitchProfileDropdown />;
};

const NewHeaderComponent: React.FC<Props> = (props) => {
    const { title, hideProfileDropdown = false, hideSwitchButton = false } = props;
    const me = getMe();
    const doc = getSingleDoctor();

    let dName = `${(doc
        ? [doc?.profile.personal.name.f, doc?.profile.personal.name.l]
        : me?.docs?.[0] && isSingleDoctorAccess()
        ? [me?.docs?.[0].personal.name.f, me?.docs?.[0].personal.name.l]
        : []
    )
        .filter(Boolean)
        .join(' ')}`.trim();

    if (dName.trim()) {
        dName = `Dr. ${dName}`;
    }

    if (!dName && me) {
        dName = `${me.user.fn}`;
    }

    const imgUrl =
        doc?.profile.personal.pic ||
        (isSingleDoctorAccess() && me?.docs?.[0]?.personal?.pic
            ? me?.docs?.[0]?.personal?.pic
            : `https://a.eka.care/user-avatar/${me?.user.oid}`);

    const inviteCode = doc?.profile.professional.invite?.invite_code;
    const inviteLink = doc?.profile.professional.invite?.invite_link;

    const showInviteCode = !!inviteCode;
    const showInviteLink = !!inviteLink;

    const message = `${dName} has invited you to eka.care. Use invite code ${doc?.profile.professional.invite?.invite_code} or click here ${doc?.profile.professional.invite?.invite_link} to consult with ${dName}. A connected healthcare experience awaits you on eka.care.`;

    return (
        <Fragment>
            <div className="flex justify-between h-61 w-full px-16 py-12 gap-8 border-b border-neutral-7">
                <p className="text-neutral-black font-400 text-20 flex-shrink-0 self-center">
                    {title}
                </p>
                {props.filterComponent ? props.filterComponent : null}
                <div className="common-elements-new-header flex-shrink-0 items-center flex gap-8">
                    {!hideProfileDropdown && (
                        <ProfileDropdown
                            dName={dName}
                            imgUrl={imgUrl}
                            inviteCode={inviteCode}
                            showInviteCode={showInviteCode}
                            showInviteLink={showInviteLink}
                            message={message}
                        />
                    )}
                    {!hideSwitchButton && me?.loginmeta?.switch ? (
                        <SwitchProfileDropdown />
                    ) : (
                        <NewSwitchProfileDropdown />
                    )}
                </div>
            </div>
        </Fragment>
    );
};

export default NewHeaderComponent;
